import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { products } from '../data/products';
import Badge from '../components/Badge';
import { Product, Badge as BadgeType } from '../types';

const BadgeAdminPage: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'issue' | 'revoke' | 'list'>('issue');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [badgeType, setBadgeType] = useState<BadgeType['type']>('perfect');
  const [badgeCode, setBadgeCode] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [revocationReason, setRevocationReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      const results = products.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(product.id).toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.upc && product.upc.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      
      setSearchResults(results);
      setIsLoading(false);
    }, 800);
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSearchResults([]);
    setSearchQuery('');
    
    if (activeTab === 'issue') {
      // Generate a random badge code
      const randomCode = 'GC' + String(product.id).substring(0, 3).toUpperCase() + Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      setBadgeCode(randomCode);
    }
  };

  const handleIssueBadge = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduct) {
      setErrorMessage('Please select a product');
      return;
    }
    
    if (!badgeCode.trim()) {
      setErrorMessage('Please enter a badge code');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    // Simulate API call with timeout
    setTimeout(() => {
      // In a real application, this would update the database
      console.log('Badge issued:', {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        badgeType,
        badgeCode,
        issueDate: new Date().toISOString(),
        validUntil: validUntil || undefined
      });
      
      setIsLoading(false);
      setIsSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setSelectedProduct(null);
        setBadgeType('perfect');
        setBadgeCode('');
        setValidUntil('');
      }, 3000);
    }, 1000);
  };

  const handleRevokeBadge = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduct || !selectedProduct.badge) {
      setErrorMessage('Please select a product with an active badge');
      return;
    }
    
    if (!revocationReason.trim()) {
      setErrorMessage('Please provide a reason for revocation');
      return;
    }
    
    setIsLoading(true);
    setErrorMessage('');
    
    // Simulate API call with timeout
    setTimeout(() => {
      // In a real application, this would update the database
      console.log('Badge revoked:', {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        badgeCode: selectedProduct.badge?.code,
        revocationReason,
        revocationDate: new Date().toISOString()
      });
      
      setIsLoading(false);
      setIsSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setSelectedProduct(null);
        setRevocationReason('');
      }, 3000);
    }, 1000);
  };

  // Get all products with badges
  const productsWithBadges = products.filter(product => product.badge);

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Badge Administration</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Issue, manage, and revoke GoodCart badges for products that meet our quality standards.
            </p>
          </motion.div>
          
          {/* Admin Panel */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 flex">
              <button
                className={`mr-4 px-4 py-2 font-medium rounded-md ${activeTab === 'issue' ? 'bg-brand-green-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => {
                setActiveTab('issue');
                setSelectedProduct(null);
                setSearchResults([]);
              }}
              >
                Issue Badge
              </button>
              <button
                className={`px-4 py-2 font-medium rounded-md ${activeTab === 'revoke' ? 'bg-red-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                onClick={() => {
                  setActiveTab('revoke');
                  setSelectedProduct(null);
                  setSearchResults([]);
                }}
              >
                Revoke Badge
              </button>
            <button
              className={`ml-4 px-4 py-2 font-medium rounded-md ${activeTab === 'list' ? 'bg-blue-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              onClick={() => {
                setActiveTab('list');
                setSelectedProduct(null);
                setSearchResults([]);
              }}
            >
              All Badges
            </button>
            </div>
            
            <div className="p-6">
              {/* Product Search */}
              {!selectedProduct && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-md font-medium text-gray-900 mb-4">
                    Step 1: {activeTab === 'issue' ? 'Select a Product' : 'Select a Product with Badge'}
                  </h3>
                  
                  <form onSubmit={handleSearch} className="mb-6">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by product name, ID, or UPC"
                        className="flex-1 rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Searching...' : 'Search'}
                      </button>
                    </div>
                  </form>
                  
                  {searchResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Product
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Health Score
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Current Badge
                            </th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {searchResults.map(product => {
                            // For revoke tab, only show products with badges
                            if (activeTab === 'revoke' && !product.badge) {
                              return null;
                            }
                            
                            return (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="h-10 w-10 flex-shrink-0">
                                      <img 
                                        className="h-10 w-10 rounded-full object-cover" 
                                        src={product.image} 
                                        alt={product.name}
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.src = `https://placehold.co/100x100?text=${product.name.charAt(0)}&font=poppins`;
                                        }}
                                      />
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                      <div className="text-sm text-gray-500">ID: {product.id}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{product.healthScore}/100</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {product.badge ? (
                                    <Badge badge={product.badge} size="sm" />
                                  ) : (
                                    <span className="text-sm text-gray-500">None</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleSelectProduct(product)}
                                    className={activeTab === 'issue' ? "text-brand-green-600 hover:text-brand-green-900" : "text-red-600 hover:text-red-900"}
                                  >
                                    Select
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      
                      {activeTab === 'revoke' && searchResults.filter(p => p.badge).length === 0 && (
                        <div className="p-4 text-center text-gray-500">
                          No products with badges found. Try a different search.
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
              
              {/* List All Badges */}
              {activeTab === 'list' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-md font-medium text-gray-900 mb-4">All Issued Badges</h3>
                  
                  {productsWithBadges.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-600">No badges have been issued yet.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valid Until</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {productsWithBadges.map((product) => (
                            <tr key={product.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 flex-shrink-0">
                                    <img 
                                      className="h-10 w-10 rounded-full object-cover" 
                                      src={product.image} 
                                      alt={product.name}
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = `https://placehold.co/100x100?text=${product.name.charAt(0)}&font=poppins`;
                                      }}
                                    />
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                    <div className="text-sm text-gray-500">ID: {product.id}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {product.badge && (
                                  <Badge badge={product.badge} size="sm" />
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-900">{product.badge?.code}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-900">
                                  {product.badge?.issueDate ? new Date(product.badge.issueDate).toLocaleDateString() : '-'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm text-gray-900">
                                  {product.badge?.validUntil ? new Date(product.badge.validUntil).toLocaleDateString() : 'No expiration'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => {
                                    setSelectedProduct(product);
                                    setActiveTab('revoke');
                                  }}
                                  className="text-red-600 hover:text-red-900 mr-4"
                                >
                                  Revoke
                                </button>
                                <a 
                                  href={`/badge-validation?code=${product.badge?.code}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  Validate
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-blue-800">Badge Management</h4>
                        <p className="text-xs text-blue-700 mt-1">
                          This list shows all products that have been issued badges. You can revoke badges if needed or validate them to check their status.
                          In a production environment, this data would be pulled from the database.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* Issue Badge Form */}
              {selectedProduct && activeTab === 'issue' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-md font-medium text-gray-900">Step 2: Configure Badge</h3>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Back to product selection
                    </button>
                  </div>
                  
                  <div className="flex flex-col md:flex-row gap-6 mb-6">
                    <div className="w-full md:w-1/3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="aspect-w-1 aspect-h-1 mb-4">
                          <img 
                            src={selectedProduct.image} 
                            alt={selectedProduct.name}
                            className="w-full h-full object-cover rounded-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://placehold.co/300x300@3x?text=${selectedProduct.name.replace(/ /g, '+')}&font=poppins`;
                            }}
                          />
                        </div>
                        <h4 className="font-medium text-gray-900">{selectedProduct.name}</h4>
                        <p className="text-sm text-gray-500 mb-2">ID: {selectedProduct.id}</p>
                        <div className="flex justify-between text-sm">
                          <span>Health Score:</span>
                          <span className="font-medium">{selectedProduct.healthScore}/100</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Rating:</span>
                          <span className="font-medium">{selectedProduct.rating} ★</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <form onSubmit={handleIssueBadge}>
                        {errorMessage && (
                          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                            {errorMessage}
                          </div>
                        )}
                        
                        <div className="mb-4">
                          <label htmlFor="badgeType" className="block text-sm font-medium text-gray-700 mb-1">
                            Badge Type
                          </label>
                          <select
                            id="badgeType"
                            value={badgeType}
                            onChange={(e) => setBadgeType(e.target.value as BadgeType['type'])}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                          >
                            <option value="perfect">GoodCart Perfect (100% Score)</option>
                            <option value="verified">Verified</option>
                            <option value="organic">Organic</option>
                            <option value="sustainable">Sustainable</option>
                          </select>
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="badgeCode" className="block text-sm font-medium text-gray-700 mb-1">
                            Badge Code
                          </label>
                          <input
                            type="text"
                            id="badgeCode"
                            value={badgeCode}
                            onChange={(e) => setBadgeCode(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            This code will be used for badge validation.
                          </p>
                        </div>
                        
                        <div className="mb-6">
                          <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700 mb-1">
                            Valid Until (Optional)
                          </label>
                          <input
                            type="date"
                            id="validUntil"
                            value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave blank for badges with no expiration date.
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading || isSuccess}
                          >
                            {isLoading ? 'Processing...' : isSuccess ? 'Badge Issued!' : 'Issue Badge'}
                          </button>
                          
                          <div className="flex-1">
                            {isSuccess && (
                              <motion.div 
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="text-brand-green-600 flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Badge successfully issued!
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                  
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Badge Preview</h4>
                    <Badge 
                      badge={{
                        type: badgeType,
                        code: badgeCode || 'EXAMPLE',
                        issueDate: new Date().toISOString(),
                        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined
                      }}
                      showDetails
                    />
                  </div>
                </motion.div>
              )}
              
              {/* Revoke Badge Form */}
              {selectedProduct && activeTab === 'revoke' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-md font-medium text-gray-900">Step 2: Confirm Badge Revocation</h3>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      ← Back to product selection
                    </button>
                  </div>
                  
                  <div className="flex flex-col md:flex-row gap-6 mb-6">
                    <div className="w-full md:w-1/3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="aspect-w-1 aspect-h-1 mb-4">
                          <img 
                            src={selectedProduct.image} 
                            alt={selectedProduct.name}
                            className="w-full h-full object-cover rounded-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://placehold.co/300x300@3x?text=${selectedProduct.name.replace(/ /g, '+')}&font=poppins`;
                            }}
                          />
                        </div>
                        <h4 className="font-medium text-gray-900">{selectedProduct.name}</h4>
                        <p className="text-sm text-gray-500 mb-2">ID: {selectedProduct.id}</p>
                        
                        {selectedProduct.badge && (
                          <div className="mt-3 p-2 border border-gray-200 rounded-md">
                            <p className="text-sm font-medium text-gray-700 mb-1">Current Badge:</p>
                            <Badge badge={selectedProduct.badge} size="sm" />
                            <p className="text-xs text-gray-500 mt-2">
                              Code: {selectedProduct.badge.code}<br />
                              Issued: {new Date(selectedProduct.badge.issueDate).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <form onSubmit={handleRevokeBadge}>
                        {errorMessage && (
                          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                            {errorMessage}
                          </div>
                        )}
                        
                        <div className="mb-6">
                          <label htmlFor="revocationReason" className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Revocation <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="revocationReason"
                            name="revocationReason"
                            rows={4}
                            value={revocationReason}
                            onChange={(e) => setRevocationReason(e.target.value)}
                            className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                            placeholder="Explain why this badge is being revoked (e.g., ingredient changes, quality issues, etc.)"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            This information will be stored for internal records but will not be publicly displayed.
                          </p>
                        </div>
                        
                        <div className="bg-red-50 p-4 rounded-md mb-6">
                          <div className="flex items-start">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-600 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                              <h4 className="text-sm font-medium text-red-800">Warning: This action cannot be undone</h4>
                              <p className="text-xs text-red-700 mt-1">
                                Revoking a badge will immediately invalidate it across all platforms where it's displayed. 
                                The badge will no longer verify as valid, and any JavaScript integrations will show a revoked status.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <button
                            type="submit"
                            className="btn bg-red-600 hover:bg-red-700 text-white"
                            disabled={isLoading || isSuccess}
                          >
                            {isLoading ? 'Processing...' : isSuccess ? 'Badge Revoked!' : 'Revoke Badge'}
                          </button>
                          
                          <div className="flex-1">
                            {isSuccess && (
                              <motion.div 
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="text-red-600 flex items-center"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Badge successfully revoked!
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgeAdminPage;